/**
 * "THIS ONE NEEDS YOU", ON THE POLICY ITSELF.
 *
 * Every acknowledgement email links to the policy page, because that is where
 * the document is. Until 2026-09-23 the page then told the reader that
 * acknowledgement was required, as a fact in a list of facts, and gave them no
 * way to do it: they had to know to go back to "Your tasks" and find the row.
 * Finding R8.
 *
 * IT OFFERS NOTHING IT DID NOT ALREADY OWE. The requirement is the
 * authorization, here as on the server: this reads the person's OWN
 * acknowledgement records, matches one to this policy AND to the version in
 * force, and offers the attestation only when that requirement is actionable.
 * A policy that generally requires acknowledgement but was never assigned to
 * this person offers nothing, because they were not asked. A requirement that
 * was closed offers nothing, because it is closed. And the button is a link
 * into the page that already exists rather than a second way to attest.
 */

import { Link } from 'react-router-dom';
import { useAuth } from '../../auth/useAuth';
import { formatDate } from '../policies/policyTable';
import {
  closedBecause,
  isOutstanding,
  requirementFor,
  useMyAcknowledgements,
} from './useAcknowledgements';
import styles from '../../shared/Page.module.css';

export function AcknowledgeCallout({
  policyId,
  currentVersionId,
  policyStatus,
}: {
  policyId: string;
  currentVersionId: string | null;
  policyStatus: string;
}) {
  const { session } = useAuth();
  const acks = useMyAcknowledgements(session?.orgId ?? null, session?.uid ?? null);

  // A policy that is not in force asks nobody to read it, whatever a stale
  // requirement may still say.
  if (policyStatus !== 'published') return null;

  const record = requirementFor(acks.data, policyId, currentVersionId);
  if (!record) return null;

  const version = record.versionNumber != null ? `version ${record.versionNumber}` : 'this version';

  if (isOutstanding(record)) {
    return (
      <div className={`${styles.notice} ${styles.noticeWarn}`} role="status">
        <p style={{ margin: 0 }}>
          <strong>You are asked to read and acknowledge {version} of this policy.</strong>
          {record.dueDate && <> It is due on {formatDate(record.dueDate)}.</>}
        </p>
        <p style={{ margin: 'var(--sp-3) 0 0' }}>
          <Link to={`/acknowledge/${record.id}/${policyId}`} className={`${styles.btn} ${styles.btnPrimary}`}>
            Read and acknowledge
          </Link>
        </p>
      </div>
    );
  }

  if (record.status === 'acknowledged') {
    return (
      <div className={`${styles.notice} ${styles.noticeInfo}`} role="status">
        <p style={{ margin: 0 }}>
          You acknowledged {version} of this policy
          {record.acknowledgedAt && <> on {formatDate(record.acknowledgedAt)}</>}.
        </p>
      </div>
    );
  }

  if (record.status === 'waived') {
    return (
      <div className={`${styles.notice} ${styles.noticeInfo}`} role="status">
        <p style={{ margin: 0 }}>An administrator waived this requirement for you.</p>
      </div>
    );
  }

  if (record.status === 'cancelled') {
    return (
      <div className={`${styles.notice} ${styles.noticeInfo}`} role="status">
        <p style={{ margin: 0 }}>
          You are no longer asked to acknowledge {version}. {closedBecause(record.cancelledReason)}
        </p>
      </div>
    );
  }

  // `unknown`: fail closed. Say nothing rather than offer an attestation for a
  // state nobody has decided the meaning of.
  return null;
}
